import React, { useEffect, useMemo, useState } from 'react';
import {
  getRiConversionByDistance, getRiBookedGuests, getRiParkMarker,
} from '../../../api/auth';
import { RiLoading, RiEmpty, RiError, RiSparseNotice, RiPagination } from '../primitives/States';
import { useReload } from '../lib/loadState';
import { marketAnnotations, marketNarrative } from '../lib/insights';
import { BUCKET_COLORS } from '../lib/driveBucket';
import { normalizeSiteCategory } from '../lib/siteCategory';
import { cleanLocationLabel, guestDisplayName } from '../lib/labelFormat';
import { fmtUsd } from '../../../utils/format';
import DriveBucketBadge from '../primitives/DriveBucketBadge';
import { RI_TABLE_FRAME, RI_THEAD, RI_TH, RI_ROW, RI_TD } from '../lib/tableStyles';

const PAGE_SIZE = 25;

// Uniform 1-decimal float formatter to match the convention used in the other
// RI tables (CongestionTab, CorridorsTab, MapOriginsTab).
const fmt1 = (v) => (v == null || Number.isNaN(Number(v)) ? '—' : Number(v).toFixed(1));
// Miles values get thousands separators to match the comma grouping used in
// the corridor / map-origins tables (markup #84). Keeps one decimal: "2,244.9".
const fmtMiles = (v) => (v == null || Number.isNaN(Number(v))
  ? '—'
  : Number(v).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 }));

// Derive Conv % directly from the row's booked/total_sessions so the
// displayed number always reconciles with the two columns to its left.
// Mirrors the fix in CorridorsTab (markup-comment #53).
function convPctFromConvRow(r) {
  const s = Number(r?.total_sessions) || 0;
  const b = Number(r?.booked) || 0;
  return s > 0 ? (b / s) * 100 : null;
}

function buildMapsUrl(originLat, originLon, parkLat, parkLon) {
  if (originLat == null || originLon == null) return null;
  if (parkLat == null || parkLon == null) {
    return `https://www.google.com/maps/search/?api=1&query=${originLat},${originLon}`;
  }
  return `https://www.google.com/maps/dir/?api=1&origin=${originLat},${originLon}&destination=${parkLat},${parkLon}&travelmode=driving`;
}

export default function MarketsTab({ clientId, range }) {
  const from = range?.from ?? null;
  const to = range?.to ?? null;
  const [conv, setConv] = useState(null);
  const [guests, setGuests] = useState(null);
  const [park, setPark] = useState(null);
  // The page index belongs to the range it was chosen under: a new range has a different
  // total, so an old page index can point past the end. Deriving the effective page from
  // the stored range key (rather than resetting it in an effect) means the booked-guests
  // fetch for a new range goes out once, at page 1, instead of once at the stale page.
  const rangeKey = `${from ?? ''}|${to ?? ''}`;
  const [pageState, setPageState] = useState({ page: 1, rangeKey });
  const page = pageState.rangeKey === rangeKey ? pageState.page : 1;
  // Takes a page number only (RiPagination's `onPageChange` contract) — not an updater function.
  const setPage = (next) => setPageState({ page: next, rangeKey });
  const [loadingConv, setLoadingConv] = useState(true);
  const [loadingGuests, setLoadingGuests] = useState(true);
  const [convFailed, setConvFailed] = useState(false);
  const [guestsFailed, setGuestsFailed] = useState(false);
  const [convReloadKey, retryConv] = useReload();
  const [guestsReloadKey, retryGuests] = useReload();

  useEffect(() => {
    let cancelled = false;
    setLoadingConv(true);
    setConvFailed(false);
    getRiConversionByDistance(clientId, { from, to })
      .then((r) => { if (!cancelled) { setConv(r?.data || []); setLoadingConv(false); } })
      .catch(() => { if (!cancelled) { setConv([]); setConvFailed(true); setLoadingConv(false); } });
    return () => { cancelled = true; };
  }, [clientId, from, to, convReloadKey]);

  useEffect(() => {
    let cancelled = false;
    getRiParkMarker(clientId)
      .then((r) => { if (!cancelled) setPark(r?.data || null); })
      .catch(() => { if (!cancelled) setPark(null); });
    return () => { cancelled = true; };
  }, [clientId]);

  useEffect(() => {
    let cancelled = false;
    setLoadingGuests(true);
    setGuestsFailed(false);
    getRiBookedGuests(clientId, { page, pageSize: PAGE_SIZE, from, to })
      .then((r) => { if (!cancelled) { setGuests(r?.data || null); setLoadingGuests(false); } })
      .catch(() => { if (!cancelled) { setGuests(null); setGuestsFailed(true); setLoadingGuests(false); } });
    return () => { cancelled = true; };
  }, [clientId, page, from, to, guestsReloadKey]);

  const annotations = useMemo(() => marketAnnotations(conv || []), [conv]);
  const narrative = useMemo(() => marketNarrative(conv || []), [conv]);
  const totalPages = guests?.total ? Math.max(1, Math.ceil(guests.total / PAGE_SIZE)) : 1;
  const parkLat = park?.latitude;
  const parkLon = park?.longitude;

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 overflow-x-auto responsive-card-table-wrapper">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Conversion by Drive Distance</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">How conversion rate and booking value change by drive-time bucket — finds your most profitable distance bands.</p>
        {loadingConv ? <RiLoading />
          : convFailed ? <RiError onRetry={retryConv} />
          : !conv || conv.length === 0 ? <RiEmpty message="No conversion-by-distance data in this date range." />
          : (
            <div className={RI_TABLE_FRAME}>
              <table className="w-full text-sm min-w-[700px] responsive-card-table">
              <thead className={RI_THEAD}>
                <tr>
                  <th className={`${RI_TH} text-left`}>Bucket</th>
                  <th className={`${RI_TH} text-right`}>Sessions</th>
                  <th className={`${RI_TH} text-right`}>Booked</th>
                  <th className={`${RI_TH} text-right`}>Conv %</th>
                  <th className={`${RI_TH} text-right`}>Avg value</th>
                  <th className={`${RI_TH} text-right`}>Avg fuel (gal)</th>
                  <th className={`${RI_TH} text-right`}>Avg congestion</th>
                </tr>
              </thead>
              <tbody className="text-gray-700 dark:text-gray-300">
                {conv.map((r) => (
                  <tr key={r.bucket} className={RI_ROW}>
                    <td className={`${RI_TD}`} data-label="Bucket">
                      <span className="inline-flex items-center">
                        <DriveBucketBadge bucket={r.bucket} />
                        {annotations.warn === r.bucket && <span className="ml-2 text-red-500 dark:text-red-400" title="Low conversion, high value">⚠</span>}
                        {annotations.star === r.bucket && <span className="ml-2 text-yellow-500 dark:text-yellow-400" title="Highest conversion">★</span>}
                        {annotations.zero === r.bucket && <span className="ml-2" title="Zero bookings">🔴</span>}
                      </span>
                    </td>
                    <td className={`${RI_TD} text-right tabular-nums`} data-label="Sessions">{r.total_sessions != null ? Number(r.total_sessions).toLocaleString() : '—'}</td>
                    <td className={`${RI_TD} text-right tabular-nums`} data-label="Booked">{r.booked != null ? Number(r.booked).toLocaleString() : '—'}</td>
                    <td className={`${RI_TD} text-right tabular-nums`} data-label="Conv %">{(() => { const c = convPctFromConvRow(r); return c != null ? `${fmt1(c)}%` : '—'; })()}</td>
                    <td className={`${RI_TD} text-right tabular-nums`} data-label="Avg value">{fmtUsd(r.avg_booking_value ?? 0, { cents: true })}</td>
                    <td className={`${RI_TD} text-right tabular-nums`} data-label="Avg fuel (gal)">{fmt1(r.avg_fuel)}</td>
                    <td className={`${RI_TD} text-right tabular-nums`} data-label="Avg congestion">{r.avg_congestion_min != null ? `${fmt1(r.avg_congestion_min)} min` : '—'}</td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
          )
        }
        {narrative && narrative.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-700 dark:text-gray-300">
            {narrative.map((n, i) => {
              const iconColor = n.icon === '⚠' ? 'text-red-500 dark:text-red-400'
                : n.icon === '★' ? 'text-yellow-500 dark:text-yellow-400'
                : '';
              return (
                <span key={i} className="inline-flex items-center gap-1.5">
                  <span aria-hidden="true" className={`select-none ${iconColor}`}>{n.icon}</span>
                  <span><strong className="text-gray-900 dark:text-white">{n.bucket}:</strong> {n.text}</span>
                </span>
              );
            })}
          </div>
        )}
        {conv && conv.length > 0 && <RiSparseNotice count={conv.length} />}
      </div>

      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Booked Guests</h3>
          {guests?.total != null && (
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {guests.total.toLocaleString()} total · page {page} / {totalPages}
            </div>
          )}
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
          Per-guest detail for every booked party with route data. Click the map icon for turn-by-turn directions.
          {' '}
          <span className="inline-flex items-center text-[9px] uppercase tracking-wide font-semibold px-1 py-0.5 rounded bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300 align-middle">
            Comp
          </span>
          {' '}flags comped, group, or zero-value bookings — counted in conversion-rate totals
          but excluded from paid-only averages, so the per-night rate shows as
          {' '}<span className="font-mono text-gray-600 dark:text-gray-300">&quot;—&quot;</span>{' '}
          (no value to divide by nights). A guest shown as
          {' '}<span className="font-mono text-gray-600 dark:text-gray-300">Anonymous</span>{' '}
          is a booked party whose first and last name the PMS sync didn&apos;t capture — the
          booking and its route are real, only the name is missing.
        </p>
        {loadingGuests ? <RiLoading />
          : guestsFailed ? <RiError onRetry={retryGuests} />
          : !guests || !guests.rows || guests.rows.length === 0 ? <RiEmpty message="No booked guests with route data in this date range." />
          : (
            <div className="overflow-x-auto responsive-card-table-wrapper">
              <div className={RI_TABLE_FRAME}>
                <table className="w-full text-sm min-w-[1200px] responsive-card-table">
                <thead className={RI_THEAD}>
                  <tr>
                    <th className={`${RI_TH} text-left`}>Guest</th>
                    <th className={`${RI_TH} text-left`}>Origin</th>
                    <th className={`${RI_TH} text-right`}>Drive</th>
                    <th className={`${RI_TH} text-right`}>Miles</th>
                    <th className={`${RI_TH} text-right`}>Fuel</th>
                    <th className={`${RI_TH} text-left`}>Highway</th>
                    <th className={`${RI_TH} text-right`}>Congestion</th>
                    <th className={`${RI_TH} text-right`}>Booking</th>
                    <th className={`${RI_TH} text-right`}>Rate/night</th>
                    <th className={`${RI_TH} text-left`}>Type</th>
                    <th className={`${RI_TH} text-left`}>Check-in</th>
                    <th className={`${RI_TH} text-right`}>Nights</th>
                    <th className={`${RI_TH} text-left`}>Party</th>
                    <th className={`${RI_TH} text-left`}>Map</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700 dark:text-gray-300">
                  {guests.rows.map((r, i) => {
                    const bucketColor = r.drive_bucket ? BUCKET_COLORS[r.drive_bucket] : null;
                    const congestHigh = r.congestion_min != null && r.congestion_min > 30;
                    const mapsUrl = buildMapsUrl(r.home_lat, r.home_lon, parkLat, parkLon);
                    const siteLabel = r.site_category ? normalizeSiteCategory(r.site_category) : null;
                    return (
                      <tr key={r.booking_session_id || i} className={RI_ROW}>
                        <td className={`${RI_TD} font-medium text-gray-900 dark:text-white`} data-label="Guest">
                          <span className="inline-flex items-center gap-1.5 flex-wrap">
                            <span>{guestDisplayName(r.guest_name, r.guest_name_is_fallback)}</span>
                            {r.booking_type === 'comp' && (
                              <span
                                className="text-[9px] uppercase tracking-wide font-semibold px-1 py-0.5 rounded bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300"
                                title="Comped or zero-value booking — counted in conversion, excluded from paid-only averages"
                              >
                                Comp
                              </span>
                            )}
                          </span>
                        </td>
                        <td className={`${RI_TD}`} data-label="Origin">{cleanLocationLabel(r.home_location)}</td>
                        <td className={`${RI_TD} text-right tabular-nums`} style={bucketColor ? { color: bucketColor } : undefined} data-label="Drive">
                          {r.drive_time_hours != null ? `${fmt1(r.drive_time_hours)} hrs` : '—'}
                        </td>
                        <td className={`${RI_TD} text-right tabular-nums`} data-label="Miles">{fmtMiles(r.drive_distance_miles)}</td>
                        <td className={`${RI_TD} text-right tabular-nums`} data-label="Fuel">{r.fuel_gallons != null ? `${fmt1(r.fuel_gallons)} gal` : '—'}</td>
                        <td className={`${RI_TD}`} data-label="Highway">{r.highway || '—'}</td>
                        <td className={`${RI_TD} text-right tabular-nums ${congestHigh ? 'text-red-600 dark:text-red-400 font-medium' : ''}`} data-label="Congestion">
                          {r.congestion_min != null ? `${fmt1(r.congestion_min)} min` : '—'}
                        </td>
                        <td className={`${RI_TD} text-right tabular-nums font-bold text-gray-900 dark:text-white`} data-label="Booking">{fmtUsd(r.reservation_value ?? 0, { cents: true })}</td>
                        <td className={`${RI_TD} text-right tabular-nums text-gray-600 dark:text-gray-300`} data-label="Rate/night">
                          {r.reservation_value != null && Number(r.num_nights) > 0
                            ? fmtUsd(Number(r.reservation_value) / Number(r.num_nights), { cents: true })
                            : '—'}
                        </td>
                        <td className={`${RI_TD}`} data-label="Type">
                          {siteLabel
                            ? <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300">{siteLabel}</span>
                            : '—'}
                        </td>
                        <td className={`${RI_TD}`} data-label="Check-in">{r.check_in_date || '—'}</td>
                        <td className={`${RI_TD} text-right tabular-nums`} data-label="Nights">{r.num_nights != null ? Number(r.num_nights).toLocaleString() : '—'}</td>
                        <td className={`${RI_TD} text-xs text-gray-500`} data-label="Party">
                          {r.num_adults ?? 0}a · {r.num_children ?? 0}c{r.num_pets ? ` · ${r.num_pets}🐾` : ''}
                        </td>
                        <td className={`${RI_TD}`} data-label="Map">
                          {mapsUrl
                            ? <a href={mapsUrl} target="_blank" rel="noreferrer" title="Open driving directions in Google Maps" className="text-base">🗺️</a>
                            : '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                </table>
              </div>
            </div>
          )
        }
        <RiPagination page={page} totalPages={totalPages} onPageChange={setPage} totalItems={guests?.total} itemLabel="guest" />
      </div>
    </div>
  );
}
